import { usePage } from '@inertiajs/react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { motion } from '@/components/public/PublicMotion';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { formatBlogCardDate } from '@/lib/formatters';

type BlogPost = {
  id: number;
  title: string;
  slug: string;
  caption?: string | null;
  publishedAt?: string | null;
  newBadge?: boolean;
  ogImage?: {
    url: string;
  } | null;
  authorName?: string | null;
};

type PageProps = {
  posts: BlogPost[];
};

export default function BlogIndex() {
  const { props } = usePage<PageProps>();
  const { posts } = props;
  const [featured, ...rest] = posts;

  return (
    <PublicLayout forceFullReloadLinks>
      <PublicSeo
        title="Blog Emovia — Intelligenza artificiale e psicologia"
        description="Approfondimenti per psicologi e professionisti della salute mentale su intelligenza artificiale, pratica clinica, sicurezza e continuità tra le sedute."
        path="/blog"
      />
      <section className="relative overflow-hidden bg-blue-lighter/60 py-12 md:py-16">
        <div className="pointer-events-none absolute -right-24 -top-24" aria-hidden="true">
          <div className="h-64 w-64 rounded-full bg-primary/5" />
          <div className="absolute inset-4 rounded-full bg-primary/5" />
          <div className="absolute inset-8 rounded-full bg-primary/5" />
        </div>

        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={staggerContainer}
            className="mx-auto max-w-2xl text-center"
          >
            <motion.span
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="mb-4 inline-block text-sm font-medium text-primary font-accent"
            >
              Blog Emovia
            </motion.span>

            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={1} font="display" className="mb-4 text-primary">
                IA e psicologia: cosa cambia nella pratica clinica
              </Heading>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="text-primary-dark"
            >
              Approfondimenti per professionisti della salute mentale su come l&apos;intelligenza
              artificiale sta trasformando il supporto tra le sedute.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="container space-y-8">
          {posts.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle headingLevel="h2">Nessun articolo disponibile.</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>Torna alla home per scoprire Emovia.</CardDescription>
              </CardContent>
              <CardFooter>
                <Button asChild>
                  <a href="/">Torna alla home</a>
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <>
              {featured && (
                <Card variant="elevated" className="overflow-hidden">
                  <div className="grid gap-8 md:grid-cols-[1.1fr_0.9fr]">
                    {featured.ogImage?.url ? (
                      <img
                        src={featured.ogImage.url}
                        alt={featured.title}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="bg-muted" />
                    )}
                    <div className="flex flex-col justify-between space-y-4">
                      <CardHeader className="space-y-3">
                        <div className="flex items-center gap-2">
                          {featured.newBadge && <Badge variant="secondary">New</Badge>}
                          {featured.publishedAt && (
                            <p className="text-muted-foreground">{formatBlogCardDate(featured.publishedAt)}</p>
                          )}
                        </div>
                        <CardTitle headingLevel="h2">{featured.title}</CardTitle>
                        {featured.caption && <CardDescription>{featured.caption}</CardDescription>}
                        {featured.authorName && (
                          <p className="text-xs text-muted-foreground">{featured.authorName}</p>
                        )}
                      </CardHeader>
                      <CardFooter className="border-t border-border">
                        <Button asChild>
                          <a href={`/blog/${featured.slug}`}>Leggi l’articolo</a>
                        </Button>
                      </CardFooter>
                    </div>
                  </div>
                </Card>
              )}

              {rest.length > 0 && (
                <div className="grid gap-6 md:grid-cols-2">
                  {rest.map((post) => (
                    <Card key={post.id} variant="interactive" className="overflow-hidden">
                      {post.ogImage?.url && (
                        <img
                          src={post.ogImage.url}
                          alt={post.title}
                          className="h-44 w-full object-cover"
                          loading="lazy"
                        />
                      )}
                      <CardHeader className="space-y-3">
                        <div className="flex items-center gap-2">
                          {post.newBadge && <Badge variant="secondary">New</Badge>}
                          {post.publishedAt && (
                            <p className="text-muted-foreground">{formatBlogCardDate(post.publishedAt)}</p>
                          )}
                        </div>
                        <CardTitle headingLevel="h3">{post.title}</CardTitle>
                        {post.caption && <CardDescription>{post.caption}</CardDescription>}
                        {post.authorName && (
                          <p className="text-xs text-muted-foreground">{post.authorName}</p>
                        )}
                      </CardHeader>
                      <CardFooter>
                        <Button asChild variant="outline">
                          <a href={`/blog/${post.slug}`}>Leggi</a>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
